import type { TFunction } from 'i18next';
import {
  parseUnitPriceInput,
  positivePriceMessageParams,
} from '~/utils/moneyBounds';
import { Building2, Truck, ShoppingCart, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { Fragment, useMemo, type ReactNode } from 'react';
import type {
  Currency,
  PlatformProduct,
  CannedTallySellerGroupsForBuyerQuery,
  UnitCode,
} from '~/graphql/graphql';
import { formatCurrency, formatUnitPrice, sellerLabel } from '~/utils';
import {
  roundUnitPrice,
  unitPriceMaxDecimalsForInput,
} from '~/utils/unitPrice';
import {
  calcUnitsPerBundleForTallyLength,
  buildTallyBundleBreakdown,
} from '~/utils/order';
import type { TableHeaderDefinition } from './SellerComparisonTableHeader';
import { PriceEditControl } from '~/components/common/PriceEditControl';
import { RemoveVendorButton } from './RemoveVendorButton';
import type { CannedTallyOption } from './SellerComparisonTable';
import {
  negotiationKey,
  type NegotiationSelection,
  type NegotiationSelectionTarget,
} from './negotiationSelection';
import {
  NegotiationCheckboxCell,
  NegotiationSellerCell,
} from './NegotiationCheckboxCell';
import { negotiationCellState } from './negotiationCellState';

const EMPTY_ACCEPTED_TALLY_PRICE = new Map<
  string,
  { amount: number; currency: Currency; unit: UnitCode }
>();
const EMPTY_EDITED_TALLY_PRICE = new Map<string, number>();

export type CannedTallyGroupData =
  CannedTallySellerGroupsForBuyerQuery['cannedTallySellerGroupsForBuyer']['content'][number];

export function CannedTallySellerGroupRow({
  group,
  headers,
  platformProduct,
  tallies,
  selectedTallyName,
  selectedLocationId,
  cartIdentifiers,
  onAddTallyToCart,
  onTallyPriceEdit,
  editedTallyPriceByKey = EMPTY_EDITED_TALLY_PRICE,
  onSuggestTallyPrice,
  pendingTallyNegotiationKeys,
  negotiationSelection,
  acceptedTallyPriceByKey = EMPTY_ACCEPTED_TALLY_PRICE,
  editingRowId,
  onEditClick,
  onEditCancel,
  onRemoveVendor,
  t,
}: Readonly<{
  group: CannedTallyGroupData;
  headers: TableHeaderDefinition[];
  platformProduct?: PlatformProduct;
  tallies: CannedTallyOption[];
  selectedTallyName?: string;
  selectedLocationId: string | undefined | null;
  cartIdentifiers: string[];
  onAddTallyToCart: (
    tallyId: string,
    tallyCount: number,
    facilityId: string,
    suggestedPrice: number | undefined,
    sellingOrganizationId: string
  ) => void;
  onTallyPriceEdit: (tallyId: string, orgId: string, newPrice: number) => void;
  editedTallyPriceByKey?: Map<string, number>;
  onSuggestTallyPrice: (target: NegotiationSelectionTarget) => void;
  pendingTallyNegotiationKeys: Set<string>;
  negotiationSelection: NegotiationSelection;
  acceptedTallyPriceByKey?: Map<
    string,
    { amount: number; currency: Currency; unit: UnitCode }
  >;
  editingRowId: string | null;
  onEditClick: (rowId: string) => void;
  onEditCancel: () => void;
  onRemoveVendor?: (organizationId: string, organizationName: string) => void;
  t: TFunction;
}>) {
  const orgId = group.facility.organization.id;
  const isEditingPrice = editingRowId === group.id;
  // A tally cart mutation explodes into one cart item per length, so no single id represents it.
  const alreadyInCart = group.lengthBreakdown.some(
    (lb) =>
      lb.platformProductsLengthId &&
      cartIdentifiers.includes([lb.platformProductsLengthId, orgId].join('-'))
  );
  const tally =
    (selectedTallyName
      ? tallies.find((td) => td.name === selectedTallyName)
      : undefined) ?? tallies[0];
  const tallyBundleBreakdown = useMemo(
    () => buildTallyBundleBreakdown(tally?.bundles ?? []),
    [tally?.bundles]
  );
  const acceptedTallyPrice = tally
    ? acceptedTallyPriceByKey.get(negotiationKey(tally.id, orgId))
    : undefined;
  const editedTallyPrice = tally
    ? editedTallyPriceByKey.get(negotiationKey(tally.id, orgId))
    : undefined;
  const tallyAvgPrice =
    editedTallyPrice ?? acceptedTallyPrice?.amount ?? group.avgPrice;
  const tallyPriceCurrency = acceptedTallyPrice?.currency ?? group.currency;
  const tallyPriceUnit = acceptedTallyPrice?.unit ?? group.priceUnit;

  const organizationLabel = sellerLabel(group.facility.organization);

  const negotiationTarget: NegotiationSelectionTarget | undefined = tally
    ? {
        kind: 'tally',
        negotiationKey: negotiationKey(tally.id, orgId),
        tallyId: tally.id,
        sellingOrganizationId: orgId,
        organizationName: group.sellerName,
        sellingOrganizationCode:
          group.facility.organization.sellingOrganizationCode,
        currencyCode: tallyPriceCurrency,
      }
    : undefined;
  const isPending = negotiationTarget
    ? pendingTallyNegotiationKeys.has(negotiationTarget.negotiationKey)
    : false;
  const negotiationState = negotiationCellState({
    rowId: group.id,
    target: negotiationTarget,
    isPending,
    selection: negotiationSelection,
    t,
  });

  const piecesPerBundle = platformProduct?.piecesPerBundle?.value;
  const tallyBundleByLengthInches = useMemo(
    () =>
      new Map(
        tallyBundleBreakdown.map((bundle) => [
          bundle.actualLengthInches,
          bundle,
        ])
      ),
    [tallyBundleBreakdown]
  );

  function pricePerBundleForLength(
    actualLengthInches: number,
    amount: number | undefined,
    unit: UnitCode
  ): number | undefined {
    if (amount == null || piecesPerBundle == null) return undefined;
    const bundle = tallyBundleByLengthInches.get(actualLengthInches);
    if (!bundle) return undefined;
    const unitsPerBundleForLength = calcUnitsPerBundleForTallyLength(
      bundle,
      piecesPerBundle,
      unit
    );
    if (unitsPerBundleForLength == null) return undefined;
    return amount * unitsPerBundleForLength;
  }

  const handleAddTallyToCart = () => {
    if (!tally) return;
    const tallyCount = Math.max(1, Math.floor(group.totalBundles));
    onAddTallyToCart(
      tally.id,
      tallyCount,
      group.facility.id,
      editedTallyPrice,
      orgId
    );
  };

  const handleTallyPriceEditSubmit = (value: string) => {
    if (!tally) return;
    const { amount } = parseUnitPriceInput(value);
    if (amount !== undefined) {
      const isUnchanged = amount === roundUnitPrice(tallyAvgPrice);
      if (!isUnchanged) onTallyPriceEdit(tally.id, orgId, amount);
      onEditCancel();
    } else {
      toast.error(
        t('order_builder.table.invalid_edit_price', {
          ...positivePriceMessageParams,
          decimals: unitPriceMaxDecimalsForInput(value, tallyAvgPrice),
        })
      );
    }
  };

  function renderGroupCell(key: string): ReactNode {
    switch (key) {
      case 'seller':
        return (
          <NegotiationSellerCell key={key} state={negotiationState}>
            <Building2 className="w-4 h-4 text-neut-400" aria-hidden="true" />
            <span className="block">
              <span className="block text-sm font-medium text-neut-900">
                {organizationLabel}
              </span>
              {group.hasBestPrice && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-prim-100 text-prim-800">
                  {t('order_builder.table.best_price')}
                </span>
              )}
            </span>
          </NegotiationSellerCell>
        );
      case 'length':
        return (
          <td key={key} className="px-6 py-4 whitespace-nowrap">
            <span className="text-sm italic text-neut-500">
              {t('order_builder.filters.canned_tally')}
            </span>
          </td>
        );
      case 'price':
        return (
          <td key={key} className="relative px-6 py-4 whitespace-nowrap">
            <div className="text-sm">
              <div className="flex items-center gap-1">
                <div>
                  <span className="font-semibold text-neut-900">
                    {formatUnitPrice(tallyAvgPrice, tallyPriceCurrency)}
                  </span>
                  <span className="text-neut-600">/{tallyPriceUnit}</span>
                </div>
                {tally && (
                  <PriceEditControl
                    isEditing={isEditingPrice}
                    isEdited={editedTallyPrice != null}
                    defaultValue={roundUnitPrice(tallyAvgPrice)}
                    onEditClick={() => onEditClick(group.id)}
                    onSubmit={handleTallyPriceEditSubmit}
                    onCancel={onEditCancel}
                    editTitle={t('order_builder.table.edit_price_title')}
                    editedTitle={t('order_builder.table.price_edited_title')}
                    editedAriaLabel={t(
                      'order_builder.table.price_edited_aria_label'
                    )}
                  />
                )}
              </div>
            </div>
          </td>
        );
      case 'stock':
        return (
          <td key={key} className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm">
              <span className="font-medium text-neut-900">
                {Math.round(group.totalBundles)}{' '}
                {t('order_builder.inventory.tallies')}
              </span>
            </div>
          </td>
        );
      case 'leadTime':
        return (
          <td key={key} className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center text-sm">
              <Truck
                className="w-3.5 h-3.5 text-neut-400 mr-1"
                aria-hidden="true"
              />
              <span className="text-neut-900">
                {group.leadTimeDays != null
                  ? `${group.leadTimeDays} ${t('order_builder.days')}`
                  : 'N/A'}
              </span>
            </div>
          </td>
        );
      case 'actions':
        return (
          <td
            key={key}
            className="sticky right-0 px-6 py-4 whitespace-nowrap bg-neut-light group-hover:bg-neut-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          >
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleAddTallyToCart}
                title={t('order_builder.table.add_to_cart_title')}
                aria-label={t('order_builder.table.add_to_cart_title')}
                disabled={
                  !selectedLocationId ||
                  !tally ||
                  (!group.hasAnyPricedLength &&
                    editedTallyPrice == null &&
                    acceptedTallyPrice == null) ||
                  alreadyInCart
                }
                className="p-2 rounded-lg transition-colors bg-prim text-neut-light hover:bg-prim-hover disabled:bg-neut-200 disabled:text-neut-400 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="w-4 h-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!negotiationTarget) return;
                  onSuggestTallyPrice(negotiationTarget);
                }}
                title={t('order_builder.table.negotiate_price_title')}
                aria-label={t('order_builder.table.negotiate_price_title')}
                disabled={!negotiationTarget || isPending}
                className="p-2 rounded-lg transition-colors border border-neut-300 text-neut-700 hover:bg-neut-50 disabled:bg-neut-200 disabled:text-neut-400 disabled:cursor-not-allowed"
              >
                <MessageSquare className="w-4 h-4" aria-hidden="true" />
              </button>
              <RemoveVendorButton
                organizationId={orgId}
                organizationName={organizationLabel}
                onClick={() => onRemoveVendor?.(orgId, organizationLabel)}
                t={t}
              />
            </div>
          </td>
        );
      default:
        return null;
    }
  }

  return (
    <Fragment>
      <tr className="group hover:bg-neut-50 transition-colors">
        <NegotiationCheckboxCell
          state={negotiationState}
          sellerName={organizationLabel}
        />
        {headers.map((header) => renderGroupCell(header.key))}
      </tr>

      <tr className="bg-neut-50/70 border-b border-neut-border">
        <td className="px-6 py-2" />
        <td colSpan={headers.length} className="px-6 py-2">
          <div className="flex items-center gap-3">
            {group.lengthBreakdown.map((lb) => {
              const editedPrice = pricePerBundleForLength(
                lb.actualLengthInches,
                editedTallyPrice,
                tallyPriceUnit
              );
              const acceptedPrice = pricePerBundleForLength(
                lb.actualLengthInches,
                acceptedTallyPrice?.amount,
                tallyPriceUnit
              );
              const perLengthPrice = editedPrice ?? acceptedPrice;
              const displayPrice =
                perLengthPrice != null
                  ? formatCurrency(perLengthPrice, tallyPriceCurrency)
                  : lb.pricePerBundle
                    ? formatCurrency(lb.pricePerBundle.amount, group.currency)
                    : null;
              return (
                <div
                  key={lb.platformProductsLengthId}
                  className="flex flex-col items-center gap-0.5"
                >
                  <span className="text-xs font-medium text-neut-600">
                    {lb.ft}ft
                  </span>
                  <div className="flex flex-col items-center text-xs leading-tight">
                    <span className="text-neut-500">
                      {lb.hasInventory ? (
                        <span className="font-semibold text-neut-800">
                          {displayPrice ?? 'N/A'}
                        </span>
                      ) : (
                        <span className="text-neut-400 italic font-medium">
                          {t('order_builder.inventory.not_applicable')}
                        </span>
                      )}
                    </span>
                    <span className="text-neut-400">
                      {lb.hasInventory
                        ? `${Math.round(lb.bundles)} ${t('order_builder.inventory.bundles')}`
                        : '—'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </td>
      </tr>
    </Fragment>
  );
}
